import React, { useState, useEffect } from 'react';
import { 
    Bot, 
    Plus, 
    Trash2, 
    Save, 
    MessageSquare, 
    Settings, 
    Terminal, 
    CheckCircle2, 
    XCircle,
    Copy,
    ExternalLink,
    ChevronRight,
    Search,
    RefreshCw,
    Link as LinkIcon,
    Bell,
    UserCheck,
    Phone,
    Type,
    Layers,
    Layout,
    MoreHorizontal,
    PlusCircle,
    Smartphone,
    Share2,
    Target,
    Sparkles,
    Cpu,
    Eye,
    EyeOff,
    Settings2,
    Shield,
    Send
} from 'lucide-react';
import client from '../api/client';
import { toast } from 'react-hot-toast';

const Bots = () => {
    const [bots, setBots] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeBot, setActiveBot] = useState(null);
    const [funnelSteps, setFunnelSteps] = useState([]);
    const [editingBot, setEditingBot] = useState(null);
    const [activeTab, setActiveTab] = useState('settings'); // 'settings', 'funnel', 'chats'
    const [showToken, setShowToken] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [botChats, setBotChats] = useState([]);

    useEffect(() => {
        fetchBots();
    }, []);

    const fetchBots = async () => {
        setLoading(true);
        try {
            const res = await client.get('/bots');
            setBots(res.data);
            if (res.data.length > 0 && !activeBot) {
                // Keep selection if exists, else select first
            }
        } catch (err) {
            toast.error('Ошибка загрузки ботов');
        } finally {
            setLoading(false);
        }
    };

    const fetchFunnel = async (botId) => {
        try {
            const res = await client.get(`/bots/${botId}/funnel`);
            // Parse JSON fields
            const parsedSteps = res.data.map(step => ({
                ...step,
                buttons: step.buttons_json ? JSON.parse(step.buttons_json) : [],
                metadata: step.metadata_json ? JSON.parse(step.metadata_json) : {}
            }));
            setFunnelSteps(parsedSteps);
        } catch (err) {
            toast.error('Ошибка загрузки воронки');
        }
    };

    const handleCreateBot = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const data = Object.fromEntries(formData);
        data.is_active = true; // Force active on creation

        try {
            await client.post('/bots', data);
            toast.success('Бот успешно добавлен');
            fetchBots();
            setEditingBot(null);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Ошибка добавления бота');
        }
    };

    const handleTestBot = async (id) => {
        try {
            await toast.promise(
                client.post(`/bots/${id}/test`),
                {
                    loading: 'Проверка соединения...',
                    success: (res) => {
                        if (res.data.ok) {
                            return `Успешно! Бот: @${res.data.result.username}`;
                        } else {
                            throw new Error(res.data.error || 'Ошибка API');
                        }
                    },
                    error: (err) => `Ошибка: ${err.message || 'не удалось связаться с ботом'}`
                },
                {
                    style: {
                        minWidth: '300px',
                        background: '#18181b',
                        color: '#fff',
                        border: '1px solid #27272a',
                        fontSize: '12px'
                    },
                }
            );
        } catch (err) {
            // Error is handled by toast.promise
        }
    };

    const handleTestAdminChat = async (id, chatId) => {
        if (!chatId) {
            toast.error('Сначала укажите Admin Chat ID');
            return;
        }
        try {
            await toast.promise(
                client.post(`/bots/${id}/test-admin-chat?admin_chat_id=${encodeURIComponent(chatId)}`),
                {
                    loading: 'Отправка тестового уведомления...',
                    success: 'Уведомление отправлено! Проверьте группу.',
                    error: 'Ошибка при отправке уведомления'
                }
            );
        } catch (err) {
            // Error handled by toast
        }
    };

    const handleUpdateBot = async (id, data) => {
        // [SECURITY] Protection against saving dots/masking characters
        if (data.token && (data.token.includes('●') || data.token.includes('***'))) {
            console.warn('Blocked attempt to save masked token chars');
            return;
        }

        try {
            setIsSaving(true);
            await client.patch(`/bots/${id}`, data);
            toast.success('Настройки обновлены');
            fetchBots();
        } catch (err) {
            toast.error('Ошибка обновления');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteBot = async (id) => {
        if (!window.confirm('Вы уверены, что хотите удалить этого бота?')) return;
        try {
            await client.delete(`/bots/${id}`);
            toast.success('Бот удален');
            fetchBots();
            if (activeBot?.id === id) setActiveBot(null);
        } catch (err) {
            toast.error('Ошибка удаления');
        }
    };

    const handleSaveFunnel = async (botId, stepsToSave = funnelSteps) => {
        const payload = stepsToSave.map(step => ({
            ...step,
            buttons_json: JSON.stringify(step.buttons || []),
            metadata_json: JSON.stringify(step.metadata || {})
        }));
        
        try {
            await client.post(`/bots/${botId}/funnel`, payload);
            toast.success('Воронка сохранена');
            fetchFunnel(botId);
        } catch (err) {
            toast.error('Ошибка сохранения');
        }
    };

    const handleGenerateFunnel = async (botId) => {
        if (!window.confirm(`ВНИМАНИЕ! Это действие удалит все текущие шаги воронки для бота ID:${botId} и создаст стандартную воронку Million Connections. Продолжить?`)) return;
        try {
            await client.post(`/bots/${botId}/generate-mcc-funnel`);
            toast.success('Воронка успешно сгенерирована!');
            fetchFunnel(botId);
            setActiveTab('funnel');
        } catch (err) {
            toast.error('Ошибка при генерации воронки');
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Скопировано');
    };

    const updateLocalStep = (stepId, updates) => {
        setFunnelSteps(prev => prev.map(s => s.id === stepId ? { ...s, ...updates } : s));
    };

    const fetchBotChats = async (botId) => {
        try {
            const res = await client.get(`/bots/${botId}/chats`);
            setBotChats(res.data);
        } catch (err) {
            toast.error('Ошибка загрузки чатов');
        }
    };

    const handleAddChat = async (e) => {
        e.preventDefault();
        const chat_id = e.target.chat_id.value;
        const title = e.target.title.value;
        try {
            await client.post(`/bots/${activeBot.id}/chats`, { chat_id, title });
            toast.success('Чат привязан');
            e.target.reset();
            fetchBotChats(activeBot.id);
        } catch (err) {
            toast.error('Ошибка привязки');
        }
    };

    const handleRemoveChat = async (chatDbId) => {
        if (!window.confirm('Отвязать этот чат от бота? Статистика участников сохранится.')) return;
        try {
            await client.delete(`/bots/chats/${chatDbId}`);
            toast.success('Чат отвязан');
            fetchBotChats(activeBot.id);
        } catch (err) {
            toast.error('Ошибка удаления');
        }
    };

    return (
        <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in duration-500 pb-20">
            {/* Header section */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
                        <div className="p-2 bg-blue-600/20 rounded-xl border border-blue-500/20 text-blue-500">
                            <Bot className="w-8 h-8" />
                        </div>
                        Конструктор Ботов
                        <span className="text-[10px] bg-zinc-800 text-zinc-500 px-2 py-0.5 rounded-full font-mono mt-2 self-start border border-zinc-700">v2026.03.31.V5</span>
                    </h1>
                    <p className="text-zinc-500 mt-2 font-medium">Создавайте сложные воронки и автоматизации без программирования</p>
                </div>
                
                <button 
                    onClick={() => setEditingBot({})}
                    className="btn-premium w-fit"
                >
                    <Plus className="w-5 h-5" /> Новый Бот
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Bots List */}
                <div className="lg:col-span-3 space-y-4">
                    <div className="premium-card p-0 overflow-hidden sticky top-24">
                        <div className="p-4 border-b border-zinc-800 flex items-center justify-between bg-zinc-900/30">
                            <h2 className="text-sm font-bold uppercase tracking-widest text-zinc-400 flex items-center gap-2">
                                <Layers className="w-4 h-4" /> Ваши Боты
                            </h2>
                            <span className="text-[10px] bg-zinc-800 px-2 py-0.5 rounded-full text-zinc-300 font-bold">{bots.length}</span>
                        </div>
                        <div className="divide-y divide-zinc-800/50 max-h-[60vh] overflow-y-auto">
                            {bots.map(bot => (
                                <div 
                                    key={bot.id}
                                    className={`relative p-4 cursor-pointer transition-all flex items-center justify-between group border flex-shrink-0 mb-2 rounded-2xl ${
                                        activeBot?.id === bot.id 
                                            ? 'bg-zinc-800 border-zinc-700 ring-1 ring-zinc-500 shadow-xl shadow-black/50' 
                                            : 'bg-zinc-900/50 border-zinc-800 hover:bg-zinc-800/80 hover:border-zinc-700'
                                    }`}
                                    onClick={() => {
                                        const botWithDefaults = {
                                            ...bot,
                                            bot_mode: bot.bot_mode || 'funnel'
                                        };
                                        setActiveBot(botWithDefaults);
                                        fetchFunnel(bot.id);
                                        setEditingBot(null);
                                        setActiveTab('settings');
                                    }}
                                >
                                    <div className="absolute top-2 right-2 text-[8px] font-mono text-zinc-700 bg-black/30 px-1.5 py-0.5 rounded uppercase tracking-tighter z-10">ID: {bot.id}</div>
                                    <div className="flex items-center gap-3">
                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-500 group-hover:rotate-6 ${
                                            activeBot?.id === bot.id ? 'bg-zinc-700 text-white shadow-lg' : 'bg-zinc-800 text-zinc-600'
                                        }`}>
                                            <Bot className="w-5 h-5" />
                                        </div>
                                        <div className="overflow-hidden">
                                            <h3 className={`font-bold text-sm truncate transition-colors ${activeBot?.id === bot.id ? 'text-white' : 'text-zinc-400 group-hover:text-zinc-200'}`}>{bot.name}</h3>
                                            <p className="text-[10px] text-zinc-600 font-mono tracking-tighter truncate opacity-60">@{bot.token?.split(':')[0] || 'unknown'}</p>
                                        </div>
                                    </div>
                                    <ChevronRight className={`w-4 h-4 transition-all duration-300 ${activeBot?.id === bot.id ? 'text-zinc-400 translate-x-1 opacity-100' : 'text-zinc-800 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5'}`} />
                                </div>
                            ))}
                            {bots.length === 0 && !loading && (
                                <div className="p-12 text-center text-zinc-500 text-sm font-medium">
                                    Нет активных ботов
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Main Content Area */}
                <div className="lg:col-span-9">
                    {!activeBot && !editingBot ? (
                        <div className="premium-card h-[400px] flex flex-col items-center justify-center text-center opacity-50 border-dashed border-2">
                            <div className="w-16 h-16 bg-zinc-900 rounded-full flex items-center justify-center mb-6">
                                <Bot className="w-8 h-8 text-zinc-600" />
                            </div>
                            <p className="font-bold text-zinc-400 text-lg">Выберите бота из списка или создайте нового</p>
                            <p className="text-zinc-600 text-sm mt-2 max-w-xs mx-auto">Чтобы начать настройку воронки, выберите конкретного бота в левой колонке</p>
                        </div>
                    ) : editingBot ? (
                        <div className="premium-card space-y-6 animate-in slide-in-from-bottom-4 duration-300">
                            <div className="flex items-center justify-between border-b border-zinc-800 pb-4">
                                <h2 className="text-xl font-bold flex items-center gap-2">
                                    <PlusCircle className="text-emerald-500" /> Подключение нового бота
                                </h2>
                                <button onClick={() => setEditingBot(null)} className="text-zinc-500 hover:text-white transition-colors p-2">
                                    <XCircle className="w-5 h-5" />
                                </button>
                            </div>
                            <form onSubmit={handleCreateBot} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Название проекта</label>
                                    <input name="name" required className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white focus:border-blue-500 outline-none transition-all placeholder:text-zinc-700" placeholder="Напр: Бот для резидентов" />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Telegram Bot API Token</label>
                                    <input name="token" required className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white focus:border-blue-500 outline-none transition-all font-mono text-sm placeholder:text-zinc-700" placeholder="712345678:AAF-..." />
                                </div>
                                <div className="space-y-2 md:col-span-1">
                                    <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Админ-чат для уведомлений (ID)</label>
                                    <input name="admin_chat_id" className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white focus:border-blue-500 outline-none transition-all placeholder:text-zinc-700 font-mono" placeholder="-100..." />
                                </div>
                                <div className="space-y-2 md:col-span-1">
                                    <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Режим работы</label>
                                    <select name="bot_mode" className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white focus:border-blue-500 outline-none transition-all">
                                        <option value="funnel">Воронка Онбординга (MCC)</option>
                                        <option value="listener">Сбор информации из каналов (Listener)</option>
                                    </select>
                                </div>
                                <div className="md:col-span-2 pt-4 border-t border-zinc-800 flex justify-end gap-4">
                                    <button type="button" onClick={() => setEditingBot(null)} className="px-6 py-3 border border-zinc-800 rounded-xl text-zinc-400 font-bold hover:bg-zinc-800/50 transition-all">Отмена</button>
                                    <button type="submit" className="btn-premium px-8">Создать и Подключить</button>
                                </div>
                            </form>
                        </div>
                    ) : (
                        <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
                            {/* Tabs Header */}
                            <div className="flex items-center gap-2 p-1 bg-zinc-900/50 rounded-xl border border-zinc-800 w-fit">
                                <button 
                                    onClick={() => setActiveTab('settings')}
                                    className={`px-6 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2 ${activeTab === 'settings' ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' : 'text-zinc-500 hover:text-zinc-300'}`}
                                >
                                    <Settings className="w-3.5 h-3.5" /> Настройки
                                </button>
                                {activeBot.bot_mode === 'funnel' && (
                                    <button 
                                        onClick={() => setActiveTab('funnel')}
                                        className={`px-6 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2 ${activeTab === 'funnel' ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' : 'text-zinc-500 hover:text-zinc-300'}`}
                                    >
                                        <Layers className="w-3.5 h-3.5" /> Воронка
                                    </button>
                                )}
                                <button 
                                    onClick={() => { setActiveTab('chats'); fetchBotChats(activeBot.id); }}
                                    className={`px-6 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2 ${activeTab === 'chats' ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' : 'text-zinc-500 hover:text-zinc-300'}`}
                                >
                                    <LinkIcon className="w-3.5 h-3.5" /> Чаты
                                </button>
                            </div>

                            {activeTab === 'settings' ? (
                                <div className="space-y-6">
                                    {/* --- HEADER --- */}
                                    <div className="premium-card flex items-center justify-between">
                                        <div className="flex items-center gap-5">
                                            <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl flex items-center justify-center font-bold text-white shadow-xl shadow-blue-900/40 text-2xl border border-white/10">
                                                {activeBot.name[0]}
                                            </div>
                                            <div className="space-y-1">
                                                <input 
                                                    type="text"
                                                    value={activeBot.name}
                                                    onChange={(e) => setActiveBot({...activeBot, name: e.target.value})}
                                                    onBlur={() => handleUpdateBot(activeBot.id, { name: activeBot.name })}
                                                    className="text-2xl font-bold text-white bg-transparent border-none focus:outline-none focus:ring-0 p-0 w-full"
                                                />
                                                <div className="flex items-center gap-3">
                                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${activeBot.is_active ? 'bg-emerald-500/10 text-emerald-500' : 'bg-zinc-800 text-zinc-500'}`}>
                                                        {activeBot.is_active ? 'Активен' : 'Пауза'}
                                                    </span>
                                                    <span className="text-[10px] text-zinc-600 font-mono">#{activeBot.id}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {isSaving && <span className="text-[10px] text-blue-500 animate-pulse font-bold uppercase mr-2">Сохранение...</span>}
                                            <button onClick={() => handleDeleteBot(activeBot.id)} className="p-3 text-zinc-600 hover:text-rose-500 hover:bg-rose-500/10 rounded-xl transition-all">
                                                <Trash2 className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                                        {/* --- LEFT COLUMN: GENERAL & SYSTEM --- */}
                                        <div className="lg:col-span-5 space-y-6">
                                            {/* SECTION: API & SECURITY */}
                                            <div className="premium-card p-6 space-y-5">
                                                <div className="flex items-center gap-2 text-zinc-400 mb-2">
                                                    <Shield className="w-4 h-4 text-blue-500" />
                                                    <h3 className="text-xs font-bold uppercase tracking-widest">API & Техчасть</h3>
                                                </div>
                                                
                                                <div className="space-y-4">
                                                    <div className="space-y-2">
                                                        <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Telegram Bot Token</label>
                                                        <div className="relative group">
                                                            <input 
                                                                type={showToken ? "text" : "password"}
                                                                value={activeBot.token || ''} 
                                                                onChange={(e) => setActiveBot({...activeBot, token: e.target.value})}
                                                                onBlur={() => handleUpdateBot(activeBot.id, { token: activeBot.token })}
                                                                className="w-full bg-zinc-950 border border-zinc-800 rounded-xl pl-4 pr-20 py-3 text-white text-xs font-mono focus:border-blue-500 outline-none transition-all" 
                                                            />
                                                            <div className="absolute right-2 top-1.5 flex items-center gap-1">
                                                                <button 
                                                                    onClick={() => setShowToken(!showToken)}
                                                                    className="p-1.5 text-zinc-500 hover:text-white transition-colors"
                                                                    title={showToken ? "Скрыть" : "Показать"}
                                                                >
                                                                    {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                                </button>
                                                                <button 
                                                                    onClick={() => copyToClipboard(activeBot.token)}
                                                                    className="p-1.5 text-zinc-500 hover:text-white transition-colors"
                                                                    title="Копировать"
                                                                >
                                                                    <Copy className="w-4 h-4" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <p className="text-[9px] text-zinc-600 leading-tight">Обновляется автоматически при изменении. Не передавайте токен посторонним.</p>
                                                        
                                                        <button 
                                                            onClick={() => handleTestBot(activeBot.id)}
                                                            className="mt-2 flex items-center gap-2 px-3 py-1.5 bg-blue-600/10 hover:bg-blue-600/20 text-blue-500 rounded-lg text-[10px] font-bold transition-all border border-blue-500/20 w-fit"
                                                        >
                                                            <RefreshCw className="w-3 h-3" /> Проверить соединение
                                                        </button>
                                                    </div>

                                                    <div className="space-y-1.5 flex-1 relative group">
                                                        <label className="text-[9px] font-bold text-zinc-600 uppercase tracking-widest flex justify-between items-center">
                                                            <span>Admin Chat ID</span>
                                                            <button 
                                                                onClick={() => handleTestAdminChat(activeBot.id, activeBot.admin_chat_id)}
                                                                className="text-blue-500 hover:text-blue-400 text-[8px] font-bold uppercase transition-all flex items-center gap-1 group-hover:opacity-100 opacity-60"
                                                            >
                                                                <Send className="w-2.5 h-2.5" />
                                                                Проверить
                                                            </button>
                                                        </label>
                                                        <input 
                                                            type="text" 
                                                            value={activeBot.admin_chat_id || ''} 
                                                            onChange={(e) => setActiveBot({...activeBot, admin_chat_id: e.target.value})}
                                                            onBlur={() => handleUpdateBot(activeBot.id, { admin_chat_id: activeBot.admin_chat_id })}
                                                            className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3 text-white text-sm focus:border-blue-500 outline-none" 
                                                            placeholder="-100..."
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* SECTION: MODE */}
                                            <div className="premium-card p-6 space-y-5">
                                                <div className="flex items-center gap-2 text-zinc-400 mb-2">
                                                    <Settings2 className="w-4 h-4 text-amber-500" />
                                                    <h3 className="text-xs font-bold uppercase tracking-widest">Режим работы</h3>
                                                </div>
                                                
                                                <div className="grid grid-cols-2 gap-3">
                                                    <button 
                                                        onClick={() => {
                                                            setActiveBot({...activeBot, bot_mode: 'funnel'});
                                                            handleUpdateBot(activeBot.id, { bot_mode: 'funnel' });
                                                        }}
                                                        className={`p-4 rounded-2xl border transition-all text-left space-y-2 ${activeBot.bot_mode === 'funnel' ? 'bg-blue-600/10 border-blue-500 shadow-lg shadow-blue-500/10' : 'bg-zinc-950 border-zinc-800 hover:border-zinc-700'}`}
                                                    >
                                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${activeBot.bot_mode === 'funnel' ? 'bg-blue-500 text-white' : 'bg-zinc-800 text-zinc-500'}`}>
                                                            <Layout className="w-4 h-4" />
                                                        </div>
                                                        <div>
                                                            <div className="text-xs font-bold text-white">Воронка</div>
                                                            <div className="text-[9px] text-zinc-500 uppercase tracking-tighter">Onboarding MCC</div>
                                                        </div>
                                                    </button>

                                                    <button 
                                                        onClick={() => {
                                                            setActiveBot({...activeBot, bot_mode: 'listener'});
                                                            handleUpdateBot(activeBot.id, { bot_mode: 'listener' });
                                                        }}
                                                        className={`p-4 rounded-2xl border transition-all text-left space-y-2 ${activeBot.bot_mode === 'listener' ? 'bg-emerald-600/10 border-emerald-500 shadow-lg shadow-emerald-500/10' : 'bg-zinc-950 border-zinc-800 hover:border-zinc-700'}`}
                                                    >
                                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${activeBot.bot_mode === 'listener' ? 'bg-emerald-500 text-white' : 'bg-zinc-800 text-zinc-500'}`}>
                                                            <Target className="w-4 h-4" />
                                                        </div>
                                                        <div>
                                                            <div className="text-xs font-bold text-white">Listener</div>
                                                            <div className="text-[9px] text-zinc-500 uppercase tracking-tighter">Сбор данных</div>
                                                        </div>
                                                    </button>
                                                </div>
                                                
                                                <div className="flex items-center justify-between p-4 bg-zinc-950 border border-zinc-800 rounded-2xl">
                                                    <span className="text-[11px] font-bold text-zinc-400 uppercase tracking-widest">Статус бота</span>
                                                    <label className="relative inline-flex items-center cursor-pointer">
                                                        <input 
                                                            type="checkbox" 
                                                            checked={activeBot.is_active} 
                                                            onChange={(e) => {
                                                                const val = e.target.checked;
                                                                setActiveBot({...activeBot, is_active: val});
                                                                handleUpdateBot(activeBot.id, { is_active: val });
                                                            }}
                                                            className="sr-only peer" 
                                                        />
                                                        <div className="w-11 h-6 bg-zinc-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>

                                        {/* --- RIGHT COLUMN: CONTENT & LINKS --- */}
                                        <div className="lg:col-span-7 space-y-6">
                                            {(activeBot.bot_mode === 'funnel' || !activeBot.bot_mode) ? (
                                                <div className="premium-card p-6 space-y-6">
                                                    <div className="flex items-center justify-between border-b border-zinc-800 pb-4">
                                                        <div className="flex items-center gap-2 text-zinc-400">
                                                            <MessageSquare className="w-4 h-4 text-blue-500" />
                                                            <h3 className="text-xs font-bold uppercase tracking-widest">Контент воронки</h3>
                                                        </div>
                                                        <button 
                                                            onClick={() => handleGenerateFunnel(activeBot.id)}
                                                            className="px-3 py-1.5 bg-blue-600/10 text-blue-500 hover:bg-blue-600 hover:text-white rounded-lg text-[10px] font-bold transition-all border border-blue-500/20"
                                                        >
                                                            Восстановить MCC Funnel
                                                        </button>
                                                    </div>

                                                    <div className="space-y-5">
                                                        <div className="space-y-2">
                                                            <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Описание (Pre-Start)</label>
                                                            <textarea 
                                                                value={activeBot.description_text || ''} 
                                                                onChange={(e) => setActiveBot({...activeBot, description_text: e.target.value})}
                                                                onBlur={() => handleUpdateBot(activeBot.id, { description_text: activeBot.description_text })}
                                                                placeholder="Текст, который видит пользователь до нажатия Start..."
                                                                className="w-full bg-zinc-950 border border-zinc-800 rounded-2xl px-5 py-4 text-zinc-200 focus:border-blue-500 outline-none transition-all min-h-[120px] resize-none text-sm placeholder:text-zinc-800" 
                                                            />
                                                        </div>
                                                        
                                                        <div className="space-y-2">
                                                            <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Приветствие (Welcome Message)</label>
                                                            <textarea 
                                                                value={activeBot.welcome_text || ''} 
                                                                onChange={(e) => setActiveBot({...activeBot, welcome_text: e.target.value})}
                                                                onBlur={() => handleUpdateBot(activeBot.id, { welcome_text: activeBot.welcome_text })}
                                                                placeholder="Первое сообщение после начала диалога..."
                                                                className="w-full bg-zinc-950 border border-zinc-800 rounded-2xl px-5 py-4 text-zinc-200 focus:border-blue-500 outline-none transition-all min-h-[120px] resize-none text-sm placeholder:text-zinc-800" 
                                                            />
                                                        </div>

                                                        <div className="grid grid-cols-2 gap-4">
                                                            <div className="space-y-1.5">
                                                                <label className="text-[9px] font-bold text-zinc-600 uppercase tracking-widest">Юзернейм Менеджера</label>
                                                                <input 
                                                                    type="text" 
                                                                    value={activeBot.manager_url || ''} 
                                                                    onChange={(e) => setActiveBot({...activeBot, manager_url: e.target.value})}
                                                                    onBlur={() => handleUpdateBot(activeBot.id, { manager_url: activeBot.manager_url })}
                                                                    className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3 text-zinc-300 text-xs focus:border-blue-500 outline-none" 
                                                                />
                                                            </div>
                                                            <div className="space-y-1.5">
                                                                <label className="text-[9px] font-bold text-zinc-600 uppercase tracking-widest">Канал для проверки подписки</label>
                                                                <input 
                                                                    type="text" 
                                                                    value={activeBot.channel_url || ''} 
                                                                    onChange={(e) => setActiveBot({...activeBot, channel_url: e.target.value})}
                                                                    onBlur={() => handleUpdateBot(activeBot.id, { channel_url: activeBot.channel_url })}
                                                                    className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-3 text-zinc-300 text-xs focus:border-blue-500 outline-none" 
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="premium-card p-12 flex flex-col items-center justify-center text-center space-y-4 min-h-[400px]">
                                                    <div className="w-20 h-20 bg-emerald-500/10 rounded-full flex items-center justify-center border border-emerald-500/20">
                                                        <Target className="w-10 h-10 text-emerald-500" />
                                                    </div>
                                                    <div className="space-y-2">
                                                        <h4 className="text-xl font-bold text-white uppercase tracking-widest">Режим Слушателя</h4>
                                                        <p className="text-sm text-zinc-500 max-w-sm leading-relaxed">В этом режиме воронка отключена. Бот сфокусирован исключительно на сборе участников из привязанных групп и каналов.</p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ) : activeTab === 'chats' ? (
                                <div className="space-y-6">
                                    <div className="premium-card space-y-6">
                                        <div className="flex items-center justify-between border-b border-zinc-800 pb-4">
                                            <h3 className="text-sm font-bold text-white flex items-center gap-2 uppercase tracking-widest leading-none">
                                                <LinkIcon className="text-blue-500 w-4 h-4" /> Привязанные Каналы и Группы
                                            </h3>
                                        </div>
                                        
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div className="space-y-4">
                                                <div className="p-4 bg-blue-600/5 border border-blue-500/10 rounded-2xl">
                                                    <p className="text-xs text-blue-400 font-medium leading-relaxed">
                                                        Привяжите каналы или группы, чтобы бот мог отслеживать вступление/выход участников и автоматически пополнять базу лидов. Бот должен быть администратором в этих чатах.
                                                    </p>
                                                </div>
                                                
                                                <form onSubmit={handleAddChat} className="space-y-4">
                                                    <div className="space-y-2">
                                                        <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Telegram Chat ID</label>
                                                        <input name="chat_id" required className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white text-sm focus:border-blue-500 outline-none" placeholder="-100..." />
                                                    </div>
                                                    <div className="space-y-2">
                                                        <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Название (для ERP)</label>
                                                        <input name="title" required className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white text-sm focus:border-blue-500 outline-none" placeholder="Основной канал" />
                                                    </div>
                                                    <button type="submit" className="w-full py-2.5 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl text-xs font-bold transition-all border border-zinc-700">Привязать Чат</button>
                                                </form>
                                            </div>

                                            <div className="space-y-3">
                                                <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Список отслеживания</label>
                                                {botChats.length > 0 ? (
                                                    <div className="space-y-2">
                                                        {botChats.map(c => (
                                                            <div key={c.id} className="flex items-center justify-between p-3 bg-zinc-900/50 border border-zinc-800 rounded-xl group">
                                                                <div>
                                                                    <p className="text-sm font-bold text-white">{c.title}</p>
                                                                    <p className="text-[10px] text-zinc-600 font-mono italic">{c.chat_id}</p>
                                                                </div>
                                                                <button onClick={() => handleRemoveChat(c.id)} className="p-2 text-zinc-700 hover:text-rose-500 hover:bg-rose-500/10 rounded-lg transition-all">
                                                                    <Trash2 size={14} />
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="py-12 text-center text-zinc-600 text-xs italic border border-dashed border-zinc-800 rounded-xl bg-zinc-900/10">
                                                        Пока нет привязанных чатов
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between bg-zinc-900 p-4 rounded-2xl border border-zinc-800">
                                        <h3 className="text-sm font-bold text-white flex items-center gap-2 uppercase tracking-widest leading-none">
                                            <Terminal className="text-blue-500 w-4 h-4" /> Шаги воронки
                                        </h3>
                                        <button 
                                            onClick={() => {
                                                const key = window.prompt("ID шага (напр: ask_phone)");
                                                if (key) {
                                                    const newSteps = [...funnelSteps, { id: Date.now(), step_key: key, message_text: "Новый текст", is_terminal: false, buttons: [], metadata: {} }];
                                                    setFunnelSteps(newSteps);
                                                    handleSaveFunnel(activeBot.id, newSteps);
                                                }
                                            }}
                                            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-bold transition-all flex items-center gap-2"
                                        >
                                            <PlusCircle className="w-4 h-4" /> Добавить шаг
                                        </button>
                                    </div>

                                    <div className="space-y-6">
                                        {funnelSteps.map((step, idx) => (
                                            <div key={step.id} className="premium-card p-0 border-l-4 border-l-zinc-800 hover:border-l-blue-600 transition-all overflow-hidden">
                                                <div className="bg-zinc-900/50 p-4 border-b border-zinc-800 flex items-center justify-between">
                                                    <div className="flex items-center gap-4">
                                                        <div className="w-8 h-8 bg-zinc-800 rounded-xl flex items-center justify-center text-xs font-bold text-zinc-400 border border-zinc-700">
                                                            {idx + 1}
                                                        </div>
                                                        <span className="text-[10px] font-black text-blue-500 bg-blue-500/10 px-2.5 py-1 rounded-full uppercase border border-blue-500/20">
                                                            ID: {step.step_key}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-3">
                                                        <label className="flex items-center gap-2 cursor-pointer bg-zinc-800/50 px-3 py-1.5 rounded-lg border border-zinc-700">
                                                            <input 
                                                                type="checkbox" 
                                                                checked={step.is_terminal}
                                                                onChange={(e) => updateLocalStep(step.id, { is_terminal: e.target.checked })}
                                                                className="w-3.5 h-3.5 accent-emerald-500" 
                                                            />
                                                            <span className="text-[10px] font-bold text-zinc-500 uppercase flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Финиш</span>
                                                        </label>
                                                        <button onClick={() => handleSaveFunnel(activeBot.id)} className="p-2 text-emerald-500 hover:bg-emerald-500/10 rounded-lg transition-all">
                                                            <Save className="w-5 h-5" />
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="p-6 space-y-6">
                                                    <div className="space-y-3">
                                                        <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest flex items-center gap-2">
                                                            <MessageSquare className="w-3.5 h-3.5" /> Текст сообщения
                                                        </label>
                                                        <textarea 
                                                            value={step.message_text}
                                                            onChange={(e) => updateLocalStep(step.id, { message_text: e.target.value })}
                                                            className="w-full bg-zinc-900 border border-zinc-800 rounded-2xl p-4 text-sm text-zinc-200 focus:border-blue-500 outline-none transition-all min-h-[100px] resize-none"
                                                        />
                                                    </div>

                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                        <div className="space-y-4">
                                                            <div className="space-y-2">
                                                                <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest flex items-center gap-2">
                                                                    <Smartphone className="w-3 h-3" /> Сохранять ответ в
                                                                </label>
                                                                <select 
                                                                    value={step.save_to_field || ''}
                                                                    onChange={(e) => updateLocalStep(step.id, { save_to_field: e.target.value })}
                                                                    className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-xs text-zinc-300 outline-none"
                                                                >
                                                                    <option value="">Не сохранять</option>
                                                                    <option value="first_name">Имя</option>
                                                                    <option value="phone">Телефон</option>
                                                                    <option value="city">Город</option>
                                                                    <option value="niche">Ниша бизнесса</option>
                                                                    <option value="turnover">Оборот (выручка)</option>
                                                                    <option value="employees_count">Количество сотрудников</option>
                                                                    <option value="inn">ИНН компании/ИП</option>
                                                                    <option value="has_consented">Согласие на обработку</option>
                                                                    <option value="goal">Цель в клубе</option>
                                                                    <option value="notes">Заметка</option>
                                                                </select>
                                                            </div>
                                                            <div className="space-y-2">
                                                                <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">Далее по умолчанию</label>
                                                                <input 
                                                                    value={step.next_step_key || ''}
                                                                    onChange={(e) => updateLocalStep(step.id, { next_step_key: e.target.value })}
                                                                    className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2 text-xs text-zinc-300 font-mono"
                                                                />
                                                            </div>

                                                            <div className="space-y-3 pt-2 border-t border-zinc-800/50">
                                                                <label className="flex items-center gap-3 cursor-pointer group/ai">
                                                                    <div className={`p-1.5 rounded-lg transition-colors ${step.use_ai ? 'bg-blue-600/20 text-blue-500' : 'bg-zinc-800 text-zinc-600'}`}>
                                                                        <Sparkles className="w-3.5 h-3.5" />
                                                                    </div>
                                                                    <span className="text-xs font-bold text-zinc-400 group-hover/ai:text-white transition-colors">Генерация текста через ИИ</span>
                                                                    <input 
                                                                        type="checkbox" 
                                                                        checked={step.use_ai || false}
                                                                        onChange={(e) => updateLocalStep(step.id, { use_ai: e.target.checked })}
                                                                        className="ml-auto w-4 h-4 accent-blue-600"
                                                                    />
                                                                </label>
                                                                
                                                                {step.use_ai && (
                                                                    <div className="space-y-2 animate-in fade-in slide-in-from-top-1 duration-300">
                                                                        <label className="text-[9px] font-black text-blue-500/70 uppercase tracking-widest ml-1">Prompt Slug (из настроек)</label>
                                                                        <input 
                                                                            value={step.ai_prompt_slug || ''}
                                                                            onChange={(e) => updateLocalStep(step.id, { ai_prompt_slug: e.target.value })}
                                                                            placeholder="funnel_welcome"
                                                                            className="w-full bg-blue-600/5 border border-blue-500/20 rounded-xl px-4 py-2 text-xs text-blue-400 font-mono focus:border-blue-500 outline-none"
                                                                        />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div className="space-y-4 p-5 bg-zinc-900/40 rounded-2xl border border-zinc-800">
                                                            <h5 className="text-[10px] font-black text-zinc-500 uppercase tracking-widest border-b border-zinc-800 pb-2 mb-2 flex items-center gap-2"><Target className="w-3 h-3" /> Автоматика</h5>
                                                            <div className="space-y-3">
                                                                <label className="flex items-center gap-3 cursor-pointer">
                                                                    <input 
                                                                        type="checkbox" 
                                                                        checked={step.metadata?.require_subscription}
                                                                        onChange={(e) => updateLocalStep(step.id, { metadata: { ...step.metadata, require_subscription: e.target.checked }})}
                                                                        className="w-4 h-4 accent-emerald-500"
                                                                    />
                                                                    <span className="text-xs font-bold text-zinc-400">Требовать подписку</span>
                                                                </label>
                                                                {step.metadata?.require_subscription && (
                                                                    <input 
                                                                        placeholder="@channel"
                                                                        value={step.metadata?.subscription_channel || ''}
                                                                        onChange={(e) => updateLocalStep(step.id, { metadata: { ...step.metadata, subscription_channel: e.target.value }})}
                                                                        className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-1.5 text-xs text-zinc-300 font-mono"
                                                                    />
                                                                )}
                                                                <label className="flex items-center gap-3 cursor-pointer">
                                                                    <Phone className="w-4 h-4 text-zinc-500" />
                                                                    <span className="text-xs font-bold text-zinc-400">Ввод номера вручную</span>
                                                                    <input 
                                                                        type="checkbox" 
                                                                        checked={step.metadata?.allow_manual_phone}
                                                                        onChange={(e) => updateLocalStep(step.id, { metadata: { ...step.metadata, allow_manual_phone: e.target.checked }})}
                                                                        className="ml-auto w-4 h-4 accent-blue-600"
                                                                    />
                                                                </label>
                                                                <div className="flex items-center justify-between gap-4 pt-2 border-t border-zinc-800">
                                                                    <span className="text-[10px] font-black text-amber-500 uppercase tracking-widest flex items-center gap-1"><Bell className="w-3 h-3" /> Напомнить (мин)</span>
                                                                    <input 
                                                                        type="number"
                                                                        value={step.metadata?.reminder_delay_minutes || ''}
                                                                        onChange={(e) => updateLocalStep(step.id, { metadata: { ...step.metadata, reminder_delay_minutes: parseInt(e.target.value) }})}
                                                                        className="w-16 bg-zinc-900 border border-zinc-800 rounded-lg px-2 py-1 text-xs text-center text-zinc-300 font-bold"
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="space-y-4 pt-6 border-t border-zinc-800">
                                                        <div className="flex items-center justify-between">
                                                            <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest flex items-center gap-2">
                                                                <Layout className="w-3.5 h-3.5 text-blue-500" /> Кнопки выбора
                                                            </label>
                                                            <button 
                                                                onClick={() => {
                                                                    const cur = step.buttons || [];
                                                                    updateLocalStep(step.id, { buttons: [...cur, [{ text: 'Кнопка', type: 'next_step', target: '' }]] });
                                                                }}
                                                                className="text-[10px] font-bold text-blue-500 hover:text-blue-400"
                                                            >
                                                                + Ряд
                                                            </button>
                                                        </div>
                                                        <div className="space-y-3">
                                                            {(step.buttons || []).map((row, rowIdx) => (
                                                                <div key={rowIdx} className="flex flex-wrap gap-2 p-3 bg-zinc-900/50 rounded-xl border border-zinc-800">
                                                                    {row.map((btn, btnIdx) => (
                                                                        <div key={btnIdx} className="flex flex-col gap-1.5 p-2 bg-zinc-800 rounded-lg border border-zinc-700 w-44 relative group/btn">
                                                                            <input 
                                                                                value={btn.text}
                                                                                onChange={(e) => {
                                                                                    const newButtons = [...step.buttons];
                                                                                    newButtons[rowIdx][btnIdx].text = e.target.value;
                                                                                    updateLocalStep(step.id, { buttons: newButtons });
                                                                                }}
                                                                                className="bg-transparent text-xs font-bold text-white focus:outline-none"
                                                                            />
                                                                            <select 
                                                                                value={btn.type}
                                                                                onChange={(e) => {
                                                                                    const newButtons = [...step.buttons];
                                                                                    newButtons[rowIdx][btnIdx].type = e.target.value;
                                                                                    updateLocalStep(step.id, { buttons: newButtons });
                                                                                }}
                                                                                className="bg-zinc-900 text-[10px] text-zinc-500 outline-none"
                                                                            >
                                                                                <option value="next_step">Шаг</option>
                                                                                <option value="url">URL</option>
                                                                                <option value="contact">Админ</option>
                                                                            </select>
                                                                            <input 
                                                                                value={btn.target}
                                                                                placeholder="Цель"
                                                                                onChange={(e) => {
                                                                                    const newButtons = [...step.buttons];
                                                                                    newButtons[rowIdx][btnIdx].target = e.target.value;
                                                                                    updateLocalStep(step.id, { buttons: newButtons });
                                                                                }}
                                                                                className="bg-zinc-900 text-[10px] text-zinc-300 rounded px-1 outline-none font-mono"
                                                                            />
                                                                            <button 
                                                                                onClick={() => {
                                                                                    const newButtons = [...step.buttons];
                                                                                    newButtons[rowIdx].splice(btnIdx, 1);
                                                                                    if (newButtons[rowIdx].length === 0) newButtons.splice(rowIdx, 1);
                                                                                    updateLocalStep(step.id, { buttons: newButtons });
                                                                                }}
                                                                                className="absolute -top-1.5 -right-1.5 p-1 bg-zinc-800 text-zinc-600 hover:text-rose-500 rounded-full opacity-0 group-hover/btn:opacity-100 transition-opacity"
                                                                            >
                                                                                <XCircle className="w-3 h-3" />
                                                                            </button>
                                                                        </div>
                                                                    ))}
                                                                    <button 
                                                                        onClick={() => {
                                                                            const cur = [...step.buttons];
                                                                            cur[rowIdx].push({ text: 'Кнопка', type: 'next_step', target: '' });
                                                                            updateLocalStep(step.id, { buttons: cur });
                                                                        }}
                                                                        className="w-8 h-8 flex items-center justify-center bg-zinc-800 rounded-lg text-zinc-400 hover:text-white"
                                                                    >
                                                                        <Plus className="w-4 h-4" />
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Bots;
